<script setup lang="ts">
/**
 * One term of a query as a chip — the option of `NxQueryInput`'s listbox, and
 * for that reason internal: it is not exported from the kit.
 *
 * The chip is an option: the text field keeps the focus and names the chip
 * through `aria-activedescendant`, so nothing in here is a tab stop and the
 * chip's accessible name (`aria-label`) says everything its parts show. What
 * the parts do with the mouse is a convenience on top of the keys; every one
 * of them has its key (or the menu) in the field, so they are no controls of
 * their own and take no focus.
 *
 * - `direct`: the parts cycle on a click — the head the occurrence, the match
 *   ending the four match modes, „Aa“ the case — and × removes. A click on the
 *   term (or anywhere else on the chip that no part took) opens the menu.
 * - `menu`: a click anywhere on the chip opens the one menu (occurrence,
 *   match, case, „vorübergehend aus“, „Entfernen“); the chip shows only what is
 *   not the default.
 *
 * In both, Enter or Space on the selected chip opens the menu.
 *
 * Presentational: the chip never changes the term. It emits what the person
 * asked for (`set-*`, `remove`) and where the menu should be (`open-menu`,
 * `close-menu`); the field owns the model and the open menu.
 */
import { computed, nextTick, ref } from 'vue';
import { NxButton } from '../Button';
import { NxChip } from '../Chip';
import NxCheckboxField from '../Inputs/NxCheckboxField.vue';
import type { NxHue } from '../lib/hue';
import type { Match, Occur, TextQueryTerm } from '../lib/textQuery';
import { NxPopover } from '../Popover';
import { NxToggleGroup, type NxToggleGroupOption } from '../ToggleGroup';
import {
  matchText,
  occurText,
  termName,
  fillTemplate,
  type NxQueryInputTexts,
  type NxQueryInteraction,
  type NxQueryWording,
} from './queryInputLabels';
import { MATCH_CYCLE, OCCUR_CYCLE, matchAllowed, nextMatch, nextOccur } from './queryTerm';

const props = defineProps<{
  term: TextQueryTerm;
  /** The id of the option element, which the field's `aria-activedescendant` names. */
  optionId: string;
  /**
   * The field this chip belongs to. The menu is teleported out of the field
   * (it must leave `NxTagField`) and carries this id, so the field can still
   * tell a focus inside the menu from a focus that left.
   */
  fieldId: string;
  interaction: NxQueryInteraction;
  wording: NxQueryWording;
  /** The chip the keys act on. */
  selected: boolean;
  /** The menu of this chip is open. */
  menuOpen: boolean;
  labels: NxQueryInputTexts;
}>();

// The id goes with every event, so the field can bind one handler for all its chips.
const emit = defineEmits<{
  'set-occur': [id: string, occur: Occur];
  'set-match': [id: string, match: Match];
  'set-case': [id: string, on: boolean];
  'set-disabled': [id: string, on: boolean];
  remove: [id: string];
  'open-menu': [id: string];
  'close-menu': [];
  /** The focus left the menu for a control outside this field. */
  'field-left': [];
}>();

/** The toggle for case: the letters read the same in every language. */
const CASE_MARK = 'Aa';

/** An occurrence is a kind, not a state: the hue only tells the three apart. */
const HUES: Record<Occur, NxHue> = { must: 'teal', mustNot: 'rose', should: 'violet' };

const direct = computed(() => props.interaction === 'direct');
const name = computed(() => termName(props.labels, props.wording, props.term));
const hue = computed(() => HUES[props.term.occur]);
// Direct: every part is there to be clicked. Menu: only what differs from the default is shown.
const showHead = computed(
  () => direct.value || props.wording === 'logic' || props.term.occur === 'mustNot'
);
const showMatch = computed(() => direct.value || props.term.match !== 'contains');
const showCase = computed(() => direct.value || props.term.caseSensitive === true);

/** The term as the match reads it: „a b“, dicht…, …dichtung. */
const shown = computed(() => {
  const { text, match } = props.term;
  if (match === 'phrase') return `„${text}“`;
  if (match === 'startsWith') return `${text}…`;
  if (match === 'endsWith') return `…${text}`;
  return text;
});

// A click on a part acts on it in the direct interaction; in the menu one it
// falls through to the popover trigger, which opens the menu.
function onHead(event: MouseEvent) {
  if (!direct.value) return;
  event.stopPropagation();
  emit('set-occur', props.term.id, nextOccur(props.term.occur));
}
function onMatch(event: MouseEvent) {
  if (!direct.value) return;
  event.stopPropagation();
  emit('set-match', props.term.id, nextMatch(props.term));
}
function onCase(event: MouseEvent) {
  if (!direct.value) return;
  event.stopPropagation();
  emit('set-case', props.term.id, props.term.caseSensitive !== true);
}

// The popover's own click opens or closes the menu, in both interactions. A part that acts in the
// direct interaction has stopped the click, so only a click that no part took (the term, the chip's
// edge) gets here.
function onPopover(open: boolean) {
  if (open) emit('open-menu', props.term.id);
  else emit('close-menu');
}

const occurOptions = computed<NxToggleGroupOption<Occur>[]>(() =>
  OCCUR_CYCLE.map((occur) => ({
    value: occur,
    label: occurText(props.labels, props.wording, occur),
  }))
);
const matchOptions = computed<NxToggleGroupOption<Match>[]>(() =>
  MATCH_CYCLE.map((match) => ({
    value: match,
    label: matchText(props.labels, match),
    disabled: !matchAllowed(props.term.text, match),
  }))
);

const occurModel = computed<Occur>({
  get: () => props.term.occur,
  set: (value) => emit('set-occur', props.term.id, value),
});
const matchModel = computed<Match>({
  get: () => props.term.match,
  set: (value) => emit('set-match', props.term.id, value),
});
const caseModel = computed<boolean>({
  get: () => props.term.caseSensitive === true,
  set: (value) => emit('set-case', props.term.id, value),
});
const disabledModel = computed<boolean>({
  get: () => props.term.disabled === true,
  set: (value) => emit('set-disabled', props.term.id, value),
});

const menuEl = ref<HTMLElement | null>(null);

/** The menu is shown: the focus goes into it, onto what is set now, so a key can change it. */
function focusMenu() {
  void nextTick(() => {
    const menu = menuEl.value;
    const target =
      menu?.querySelector<HTMLElement>('input[type="radio"]:checked') ??
      menu?.querySelector<HTMLElement>('input:not(:disabled), button:not(:disabled)');
    target?.focus();
  });
}

/** True when `next` is not inside this field or its teleported menu. */
function leftTheField(next: EventTarget | null): boolean {
  const el = next instanceof Element ? next : next instanceof Node ? next.parentElement : null;
  if (!el) return false;
  return el.closest('[data-nx-query-field]')?.getAttribute('data-nx-query-field') !== props.fieldId;
}

// A menu belongs to the focus: when it goes to another control the menu goes. Focus that goes
// nowhere (a click on text in the menu, the panel hiding) leaves it to the popover. The menu is
// teleported, so this focusout does not bubble to the field — a focus that leaves the field is
// reported on its own.
function onMenuFocusout(event: FocusEvent) {
  const next = event.relatedTarget;
  if (!(next instanceof Node) || menuEl.value?.contains(next)) return;
  if (leftTheField(next)) emit('field-left');
  else emit('close-menu');
}
</script>

<template>
  <span class="nx-query-chip">
    <NxPopover
      teleport
      :open="menuOpen"
      lazy
      side="bottom-start"
      :label="labels.menuLabel"
      @update:open="onPopover"
      @opened="focusMenu"
    >
      <template #trigger>
        <span class="nx-query-chip__pill" :class="{ 'is-disabled': term.disabled === true }">
          <NxChip
            :id="optionId"
            option
            removable
            size="xs"
            :hue="hue"
            :selected="selected"
            :remove-label="fillTemplate(labels.removeTermLabel, { term: term.text })"
            :aria-label="name"
            :data-term-id="term.id"
            data-testid="nx-query-chip"
            @remove="emit('remove', term.id)"
          >
            <span class="nx-query-chip__body">
              <span
                v-if="showHead"
                class="nx-query-chip__head"
                :class="{ 'is-action': direct }"
                :title="direct ? labels.changeOccurTitle : undefined"
                data-testid="nx-query-chip-head"
                @click="onHead"
              >
                {{ occurText(labels, wording, term.occur) }}
              </span>
              <span
                class="nx-query-chip__text"
                :class="{ 'is-excluded': term.occur === 'mustNot' }"
                data-testid="nx-query-chip-text"
              >
                {{ shown }}
              </span>
              <span
                v-if="showMatch"
                class="nx-query-chip__match"
                :class="{ 'is-action': direct }"
                :title="direct ? labels.changeMatchTitle : undefined"
                data-testid="nx-query-chip-match"
                @click="onMatch"
              >
                {{ matchText(labels, term.match) }}
              </span>
              <span
                v-if="showCase"
                class="nx-query-chip__case"
                :class="{ 'is-action': direct, 'is-on': term.caseSensitive === true }"
                :title="direct ? labels.caseSensitiveLabel : undefined"
                data-testid="nx-query-chip-case"
                @click="onCase"
              >
                {{ CASE_MARK }}
              </span>
            </span>
          </NxChip>
        </span>
      </template>
      <div
        v-if="menuOpen"
        ref="menuEl"
        class="nx-query-chip__menu"
        :data-nx-query-field="fieldId"
        data-testid="nx-query-chip-menu"
        @focusout="onMenuFocusout"
      >
        <NxToggleGroup
          v-model="occurModel"
          :options="occurOptions"
          :aria-label="labels.occurGroupLabel"
          wrap
        />
        <NxToggleGroup
          v-model="matchModel"
          :options="matchOptions"
          :aria-label="labels.matchGroupLabel"
          wrap
        />
        <NxCheckboxField v-model="caseModel" :label="labels.caseSensitiveLabel" />
        <NxCheckboxField v-model="disabledModel" :label="labels.disabledLabel" />
        <NxButton
          variant="outline"
          data-testid="nx-query-chip-menu-remove"
          @click="emit('remove', term.id)"
        >
          {{ labels.removeLabel }}
        </NxButton>
      </div>
    </NxPopover>
  </span>
</template>

<style scoped>
.nx-query-chip {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
}
/* Around NxChip: a switched-off term is dimmed here. The active-option ring is the chip's. */
.nx-query-chip__pill {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  border-radius: var(--nxt-radius-full);
}
/* Switched off: still there, visibly out of the search. A switched-off term, not a
   disabled chip — the kit's disabled look is a different state. */
.nx-query-chip__pill.is-disabled {
  opacity: 0.6;
}
.nx-query-chip__body {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  cursor: pointer;
}
.nx-query-chip__head,
.nx-query-chip__match,
.nx-query-chip__case {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
}
.nx-query-chip__head {
  font-weight: var(--nxt-font-weight-medium);
}
.nx-query-chip__text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.nx-query-chip__text.is-excluded {
  text-decoration: line-through;
}
.nx-query-chip__case {
  font-weight: var(--nxt-font-weight-medium);
  opacity: 0.6;
}
.nx-query-chip__case.is-on {
  opacity: 1;
  text-decoration: underline;
  text-underline-offset: calc(var(--nxt-hairline) * 2);
}
.nx-query-chip__head.is-action,
.nx-query-chip__match.is-action,
.nx-query-chip__case.is-action {
  padding-inline: var(--nxt-space-2xs);
  margin-inline: calc(var(--nxt-space-2xs) * -1);
}
.nx-query-chip__head.is-action:hover,
.nx-query-chip__match.is-action:hover,
.nx-query-chip__case.is-action:hover {
  background: color-mix(in srgb, currentColor 14%, transparent);
}
.nx-query-chip__menu {
  display: grid;
  gap: var(--nxt-space-sm);
  justify-items: start;
}
</style>
